import { deleteEntity, updateEntities } from '@redux/entities'
import { Schemas } from '@redux/schema'
import { normalize } from 'normalizr'

import { isValidPrice } from '@domain-group/javascript-utils'

import api from '@functions/api'

import type { InvoiceItemModel } from '@models/types'
import type {
  Action, DeleteParams, EntityOptions, Errors, UpdateParams,
} from '@redux/modules/types'
import type { AppDispatch } from '@redux/store'

import { handleError } from '../utils'

import { updateInvoiceTotal } from './invoice'

const CREATE_REQUEST = 'realbase/invoiceItem/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/invoiceItem/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/invoiceItem/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/invoiceItem/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/invoiceItem/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/invoiceItem/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/invoiceItem/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/invoiceItem/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/invoiceItem/DELETE_FAILURE'

export type InvoiceItemModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: InvoiceItemModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function createInvoiceItem(invoiceItem: Partial<InvoiceItemModel>, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      invoice_item: invoiceItem,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/invoice_items.json', options, config)
      .then(({ data }: { data: InvoiceItemModel }) => {
        const { invoice_id, invoice_total } = data
        const normalizedJson = normalize(data, Schemas.INVOICE_ITEM)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        if (invoice_total){
          dispatch(updateInvoiceTotal(invoice_id, invoice_total))
        }

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateInvoiceItem(
  invoiceItem: UpdateParams<InvoiceItemModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      invoice_item: invoiceItem,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/invoice_items/${invoiceItem.id}.json`, options, config)
      .then(({ data }: { data: InvoiceItemModel }) => {
        const { invoice_id, invoice_total } = data
        const normalizedJson = normalize(data, Schemas.INVOICE_ITEM)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        if (invoice_total){
          dispatch(updateInvoiceTotal(invoice_id, invoice_total))
        }

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

type DeleteResponse = {
  id: number,
  invoice_id: number,
  invoice_total: number,
  pending_deletion?: boolean,
}

export function deleteInvoiceItem(invoiceItem: DeleteParams<InvoiceItemModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/invoice_items/${invoiceItem.id}.json`, {}, config)
      .then(({ data }: { data: DeleteResponse }) => {
        const { invoice_id, invoice_total, pending_deletion } = data

        // Don't delete the item from the store if it's pending deletion.
        // Delete will be confirmed via a websocket message
        if (pending_deletion) return { success: true as const, data }

        const normalizedJson = normalize(data, Schemas.INVOICE_ITEM)
        // @ts-expect-error TS2345
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        if (invoice_total) dispatch(updateInvoiceTotal(invoice_id, invoice_total))

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function reprocessInvoiceItemToZuora(invoiceItem: Partial<InvoiceItemModel>) {
  const config = {
    method: 'PUT',
  }

  return (dispatch: AppDispatch) => {
    const promise = api(`/invoice_items/${invoiceItem.id}/reprocess_zuora.json`, {}, config)
      .then(({ data }: { data: Partial<InvoiceItemModel> }) => {
        const normalizedJson = normalize(data, Schemas.INVOICE_ITEM)

        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)

        return { success: false, errors }
      })
    return promise
  }
}

export function removeInvoiceItemFromStore(invoiceItem: InvoiceItemModel) {
  return (dispatch: AppDispatch) => {
    const normalizedJson = normalize({ id: invoiceItem.id }, Schemas.INVOICE_ITEM)
    // @ts-expect-error TS2345
    dispatch(deleteEntity(normalizedJson))
    dispatch(deleteSuccess())

    if (isValidPrice(invoiceItem.invoice_total)){
      // @ts-expect-error TS2345
      dispatch(updateInvoiceTotal(invoiceItem.invoice_id, invoiceItem.invoice_total))
    }

    return { success: true as const, data: invoiceItem }
  }
}

// Reducer
export default function reducer(
  state: InvoiceItemModuleState = initialState,
  action: Action = { type: '' },
): InvoiceItemModuleState {
  switch (action.type){
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
